<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Job Applications</h1>
            <p class="text-slate-400">Review and manage applications for your job postings</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="exportApplications" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export
            </button>
            <router-link to="/company/jobs" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              View Jobs
            </router-link>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Applications</p>
            <p class="text-3xl font-bold text-white">{{ applicationStats.total }}</p>
            <p class="text-sm text-emerald-400">all time</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Review</p>
            <p class="text-3xl font-bold text-white">{{ applicationStats.pending }}</p>
            <p class="text-sm text-cyan-400">awaiting</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Hired</p>
            <p class="text-3xl font-bold text-white">{{ applicationStats.hired }}</p>
            <p class="text-sm text-amber-400">successful</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Rejected</p>
            <p class="text-3xl font-bold text-white">{{ applicationStats.rejected }}</p>
            <p class="text-sm text-purple-400">not selected</p>
          </div>
        </div>
      </div>

      <!-- Filters and Search -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Search</label>
            <input 
              v-model="filters.search" 
              type="text" 
              class="form-input w-full" 
              placeholder="Search by name, job title..."
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Job</label>
            <select v-model="filters.jobId" class="form-select w-full">
              <option value="">All Jobs</option>
              <option v-for="job in jobs" :key="job.id" :value="job.id">{{ job.title }}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Status</label>
            <select v-model="filters.status" class="form-select w-full">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewing">Under Review</option>
              <option value="interviewed">Interviewed</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Date Range</label>
            <select v-model="filters.dateRange" class="form-select w-full">
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Applications List -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-700/50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Applicant</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Job Title</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Experience</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Applied</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-700/50">
              <tr v-for="application in filteredApplications" :key="application.id" class="hover:bg-slate-700/30">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center mr-3">
                      <span class="text-sm font-bold text-white">{{ application.applicant_name.charAt(0) }}</span>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-white">{{ application.applicant_name }}</div>
                      <div class="text-sm text-slate-400">{{ application.applicant_email }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-white">{{ application.job_title }}</div>
                  <div class="text-sm text-slate-400">{{ application.job_location }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-white">{{ application.experience_years }} years</div>
                  <div class="text-sm text-slate-400">{{ application.skills }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    application.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    application.status === 'reviewing' ? 'bg-cyan-500/20 text-cyan-400' :
                    application.status === 'interviewed' ? 'bg-blue-500/20 text-blue-400' :
                    application.status === 'hired' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ application.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {{ formatDate(application.applied_date) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div class="flex items-center space-x-2">
                    <button @click="viewApplication(application)" class="text-emerald-400 hover:text-emerald-300">
                      View
                    </button>
                    <button @click="updateStatus(application.id, 'reviewing')" class="text-cyan-400 hover:text-cyan-300">
                      Review
                    </button>
                    <button @click="updateStatus(application.id, 'hired')" class="text-emerald-400 hover:text-emerald-300">
                      Hire
                    </button>
                    <button @click="updateStatus(application.id, 'rejected')" class="text-red-400 hover:text-red-300">
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Application Detail Modal -->
      <div v-if="selectedApplication" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-semibold text-white">Application Details</h3>
            <button @click="selectedApplication = null" class="text-slate-400 hover:text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Applicant Info -->
            <div class="bg-slate-700/30 rounded-xl p-4">
              <h4 class="text-lg font-semibold text-white mb-4">Applicant Information</h4>
              <div class="space-y-3">
                <div>
                  <label class="text-sm font-medium text-slate-400">Name</label>
                  <p class="text-white">{{ selectedApplication.applicant_name }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Email</label>
                  <p class="text-white">{{ selectedApplication.applicant_email }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Phone</label>
                  <p class="text-white">{{ selectedApplication.applicant_phone || 'Not provided' }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Experience</label>
                  <p class="text-white">{{ selectedApplication.experience_years }} years</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Skills</label>
                  <p class="text-white">{{ selectedApplication.skills }}</p>
                </div>
              </div>
            </div>

            <!-- Job Info -->
            <div class="bg-slate-700/30 rounded-xl p-4">
              <h4 class="text-lg font-semibold text-white mb-4">Job Information</h4>
              <div class="space-y-3">
                <div>
                  <label class="text-sm font-medium text-slate-400">Job Title</label>
                  <p class="text-white">{{ selectedApplication.job_title }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Location</label>
                  <p class="text-white">{{ selectedApplication.job_location }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Salary Range</label>
                  <p class="text-white">${{ selectedApplication.job_salary_min }} - ${{ selectedApplication.job_salary_max }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Applied Date</label>
                  <p class="text-white">{{ formatDate(selectedApplication.applied_date) }}</p>
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-400">Status</label>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    selectedApplication.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    selectedApplication.status === 'reviewing' ? 'bg-cyan-500/20 text-cyan-400' :
                    selectedApplication.status === 'interviewed' ? 'bg-blue-500/20 text-blue-400' :
                    selectedApplication.status === 'hired' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ selectedApplication.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Cover Letter -->
          <div class="mt-6 bg-slate-700/30 rounded-xl p-4">
            <h4 class="text-lg font-semibold text-white mb-4">Cover Letter</h4>
            <p class="text-slate-300 whitespace-pre-wrap">{{ selectedApplication.cover_letter }}</p>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-slate-700/50">
            <button @click="selectedApplication = null" class="btn-secondary">
              Close
            </button>
            <button @click="updateStatus(selectedApplication.id, 'reviewing')" class="btn-primary">
              Mark as Reviewing
            </button>
            <button @click="updateStatus(selectedApplication.id, 'hired')" class="btn-primary bg-emerald-500 hover:bg-emerald-600">
              Hire Applicant
            </button>
            <button @click="updateStatus(selectedApplication.id, 'rejected')" class="btn-primary bg-red-500 hover:bg-red-600">
              Reject Application
            </button>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const applications = ref([])
const jobs = ref([])
const selectedApplication = ref(null)

const applicationStats = ref({
  total: 0,
  pending: 0,
  hired: 0,
  rejected: 0
})

const filters = ref({
  search: '',
  jobId: '',
  status: '',
  dateRange: ''
})

// Computed
const filteredApplications = computed(() => {
  let filtered = applications.value

  if (filters.value.search) {
    const search = filters.value.search.toLowerCase()
    filtered = filtered.filter(app => 
      app.applicant_name.toLowerCase().includes(search) ||
      app.job_title.toLowerCase().includes(search) ||
      app.applicant_email.toLowerCase().includes(search)
    )
  }

  if (filters.value.jobId) {
    filtered = filtered.filter(app => app.job_id === parseInt(filters.value.jobId))
  }

  if (filters.value.status) {
    filtered = filtered.filter(app => app.status === filters.value.status)
  }

  if (filters.value.dateRange) {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    filtered = filtered.filter(app => {
      const appliedDate = new Date(app.applied_date)
      switch (filters.value.dateRange) {
        case 'today':
          return appliedDate >= today
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
          return appliedDate >= weekAgo
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
          return appliedDate >= monthAgo
        case 'quarter':
          const quarterAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)
          return appliedDate >= quarterAgo
        default:
          return true
      }
    })
  }

  return filtered
})

// Methods
const fetchApplications = async () => {
  try {
    const response = await fetch('/api/company/applications', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      applications.value = data.applications || []
    }
  } catch (error) {
    console.error('Error fetching applications:', error)
  }
}

const fetchJobs = async () => {
  try {
    const response = await fetch('/api/company/jobs', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      jobs.value = data.jobs || []
    }
  } catch (error) {
    console.error('Error fetching jobs:', error)
  }
}

const fetchApplicationStats = async () => {
  try {
    const response = await fetch('/api/company/application-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      applicationStats.value = data
    }
  } catch (error) {
    console.error('Error fetching application stats:', error)
  }
}

const viewApplication = (application: any) => {
  selectedApplication.value = application
}

const updateStatus = async (applicationId: number, status: string) => {
  try {
    const response = await fetch('/api/company/update-application-status', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ applicationId, status })
    })
    
    if (response.ok) {
      await fetchApplications()
      await fetchApplicationStats()
      if (selectedApplication.value) {
        selectedApplication.value = null
      }
    }
  } catch (error) {
    console.error('Error updating application status:', error)
  }
}

const exportApplications = () => {
  const csvContent = [
    ['Name', 'Email', 'Job Title', 'Experience', 'Status', 'Applied Date'],
    ...filteredApplications.value.map(app => [
      app.applicant_name,
      app.applicant_email,
      app.job_title,
      `${app.experience_years} years`,
      app.status,
      formatDate(app.applied_date)
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'job-applications.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchApplications(),
    fetchJobs(),
    fetchApplicationStats()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
